import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, ArrowLeft, FileText } from 'lucide-react';

const Disclaimer = () => {
    return (
        <div className="min-h-screen bg-foreground">
            {/* Header */}
            <div className="container mx-auto px-4 py-6">
                <Link to="/">
                    <Button variant="ghost" className="mb-4">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </Link>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 sm:py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="w-16 h-16 rounded-2xl bg-primary mx-auto flex items-center justify-center mb-4">
                            <Shield className="w-8 h-8 text-primary-foreground" />
                        </div>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-background tracking-tight mb-4">
                            Disclaimer
                        </h1>
                        <p className="text-background/60">
                            Last updated: January 25, 2026
                        </p>
                    </div>

                    <div className="bg-card rounded-2xl p-8 sm:p-12 shadow-xl space-y-8 text-foreground">
                        <section>
                            <h2 className="text-2xl font-bold mb-4">Image Ownership & Rights</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                <strong>You retain full ownership of your images.</strong> Unlike some other background removal services that claim ownership or rights to your uploaded files, we respect your intellectual property rights completely.
                            </p>
                            <p className="text-muted-foreground leading-relaxed">
                                All images uploaded to our service belong to their rightful owners. We do not claim any ownership, rights, licenses, or interest in your images. Your content is yours, and it stays yours.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Image Processing & Storage</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                When you upload an image to our service:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>Your image is processed temporarily to remove the background</li>
                                <li>We do not permanently store your images on our servers</li>
                                <li>Processed images are automatically deleted after you download them</li>
                                <li>We do not use your images for training AI models or any other purpose</li>
                                <li>We do not share, sell, or distribute your images to third parties</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Your Responsibilities</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                By uploading images to our service, you confirm that:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>You own the images or have permission to use them</li>
                                <li>The images do not violate any copyright, trademark, or intellectual property rights</li>
                                <li>The images do not contain illegal, harmful, or offensive content</li>
                                <li>You will not upload personal information or sensitive data of others without consent</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Commercial Use</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                You are free to use processed images for any lawful purpose, including commercial use, provided you have the necessary rights to the original images. We impose no additional restrictions on how you use your processed images.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Service Accuracy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                While we strive to provide the best background removal results, we make no guarantees about the accuracy or quality of the processed images. Results may vary depending on image quality, complexity, and other factors. You are responsible for verifying that processed images meet your requirements before use.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Third-Party Content</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you process images that contain third-party content (such as logos, brands, or copyrighted material), you are responsible for ensuring you have the right to use and modify such content. We are not liable for any copyright infringement or intellectual property violations arising from your use of the service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Security & Data Protection</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                We implement industry-standard security measures to protect your images during upload and processing:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>All connections use HTTPS encryption</li>
                                <li>Images are processed in isolated environments</li>
                                <li>Temporary files are securely deleted after processing</li>
                                <li>We do not retain logs containing your image data</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Limitation of Liability</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We provide this service "as is" without warranties of any kind. We are not liable for any damages, losses, or legal issues arising from your use of our service, including but not limited to copyright infringement claims, data loss, or business interruption.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Changes to This Disclaimer</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We may update this disclaimer from time to time. We will notify users of any material changes by updating the "Last updated" date at the top of this page. Continued use of our service after changes constitutes acceptance of the updated disclaimer.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you have any questions about this disclaimer or your rights regarding uploaded images, please contact us at{' '}
                                <a href="mailto:info@backgroundremove.uk" className="text-primary hover:underline">
                                    info@backgroundremove.uk
                                </a>
                            </p>
                        </section>

                        <div className="mt-8 p-6 bg-primary/10 rounded-xl border border-primary/20">
                            <div className="flex items-start gap-3">
                                <FileText className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                                <div>
                                    <h3 className="font-semibold text-lg mb-2">Our Commitment to You</h3>
                                    <p className="text-muted-foreground">
                                        Unlike other services that claim ownership of your files, we believe your work belongs to you.
                                        We process your images solely for the purpose of background removal and nothing else.
                                        Your intellectual property rights remain fully intact, always.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Disclaimer;
